/* global _WHITE_LABEL */

import Immutable from 'immutable';
import { createSelector } from 'reselect';
import { whiteInstitutions } from '../../native/config/whiteLabel';
import { institutionRolesSelector } from '../Auth/selectors';
import {
  currentCourseInstitutionSelector,
  coursesSelector,
} from '../Courses/selectors';
import { currentUserSelector } from '../Users/selectors';
import { selectCurrentInstitutionPath } from './CurrentInstitutionPath/selectors';

export const selectInstitutions = (state) => state.Institutions;

export const selectAllInstitutions = createSelector(
  selectInstitutions,
  (institutions) => {
    if (institutions) {
      return institutions;
    }
    return new Immutable.Map();
  }
);

export const selectInstitutionSummariesByPath = createSelector(
  selectAllInstitutions,
  (institutions) => institutions.toJS()
);

export const selectFeaturableInstitutionsList = createSelector(
  selectInstitutions,
  (institutions) => {
    if (institutions) {
      return institutions
        .filter((i) => {
          return (
            i.get('privacy', null) === 'public' &&
            i.get('courses', 0) >= 1 &&
            i.get('logo', null)
          );
        })
        .toList();
    }
    return new Immutable.List();
  }
);

export const selectCurrentInstitution = createSelector(
  selectInstitutions,
  currentCourseInstitutionSelector,
  selectCurrentInstitutionPath,
  (institutions, currentLinkedInstitutionPath, institutionPath) => {
    if (currentLinkedInstitutionPath) {
      return institutions.get(currentLinkedInstitutionPath, undefined);
    } else if (institutionPath) {
      return institutions.get(institutionPath, undefined);
    } else {
      return null;
    }
  }
);

export const selectCurrentInstitutionFromPath = createSelector(
  selectInstitutions,
  selectCurrentInstitutionPath,
  (institutions, institutionPath) => {
    if (institutionPath) {
      return institutions.get(institutionPath, undefined);
    } else {
      return null;
    }
  }
);

export const selectHasWhiteLabelInstitutionLogo = createSelector(
  selectCurrentInstitution,
  (institution) => {
    // logoUrl could be empty string
    if (institution && institution.get('whiteLabelLogoImageUrl')) {
      return true;
    }
    return false;
  }
);

/**
 * Returns a boolean indicating whethere the current user is an admin
 * of the current institution
 */
export const selectIsAdminOfCurrentInstitution = createSelector(
  selectCurrentInstitution,
  currentUserSelector,
  (currentInstitution, currentUser) => {
    if (currentUser && currentInstitution) {
      return currentInstitution
        .get('admins')
        .includes(currentUser.get('profileName'));
    } else {
      return false;
    }
  }
);

export const selectInstitutionsIsAdminOf = createSelector(
  selectInstitutions,
  currentUserSelector,
  (institutions, currentUser) => {
    if (!currentUser || !institutions) {
      return new Immutable.List();
    }

    return institutions
      .filter((i) => {
        return i.get('admins').includes(currentUser.get('profileName'));
      })
      .toList();
  }
);

export const makeSelectInstitution = (path) =>
  createSelector([selectInstitutions], (institutions) =>
    institutions.get(path, Immutable.Map())
  );

export const makeSelectInstitutions = (paths) =>
  createSelector(selectInstitutions, (institutions) =>
    institutions.filter((i) => paths.includes(i.get('path')))
  );

export const makeSelectWhiteLabelInstitution = () => {
  if (_WHITE_LABEL) {
    const institution = Immutable.fromJS(whiteInstitutions).find((item) => {
      return (
        item.get('institutionPath').localeCompare(_WHITE_LABEL, 'en', {
          sensitivity: 'base',
        }) === 0
      );
    });
    if (institution) {
      return institution;
    }
  }
  return null;
};

export const selectCreateCourseAllowedInstitutions = createSelector(
  [institutionRolesSelector, selectInstitutions],
  (institutionRoles, institutions) => {
    return institutions
      .filter((institution) => {
        const roles = institutionRoles.get(
          institution.get('path'),
          Immutable.List()
        );
        return roles.contains('admin') || roles.contains('staff');
      })
      .toList();
  }
);

export const makeSelectInstitutionByCoursePath = (coursePath) =>
  createSelector(
    [coursesSelector, selectInstitutions],
    (courses, institutions) => {
      const course = courses.get(coursePath);
      if (course) return institutions.get(course.get('institution'));
      return null;
    }
  );
